import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { ConsentBadge } from '@/components/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { formatDateTime, normalizeText } from '@/lib/utils';

export const Route = createFileRoute('/consent/')({ component: ConsentLedger });

const LIMIT = 100;

/**
 * The consent ledger — append-only, and the evidence surface if anyone asks
 * what a given person agreed to and when.
 *
 * Note what is *not* here: no name, no email, no phone. Rows are keyed by
 * person id because the raw contact values were hashed before they ever
 * reached DMN, so there is nothing to redact.
 */
function ConsentLedger() {
  const events = useQuery({
    queryKey: queryKeys.consentEvents(LIMIT),
    queryFn: () => api.consentEvents(LIMIT),
  });

  const search = useTableSearch(events.data, (e) => [
    e.personId,
    e.accountId,
    e.placementId,
    e.type,
    e.state,
    e.copyVersion,
  ]);
  const pagination = usePagination(search.filtered);
  return (
    <>
      <PageHeader
        title="Consent ledger"
        description="Append-only record of every consent interaction"
      />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search person, practice, placement or event"
          label="Search consent events"
          noun="event"
        />
        <Card className="shadow-none">
          <CardContent className="p-0">
            {events.isError ? (
              <ErrorState error={events.error} onRetry={() => void events.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Resulting state</TableHead>
                    <TableHead>Person</TableHead>
                    <TableHead>Practice</TableHead>
                    <TableHead>Placement</TableHead>
                    <TableHead>Copy</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.isPending && <LoadingRows columns={7} />}
                  {pagination.pageRows.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTime(event.ts)}
                      </TableCell>
                      <TableCell className="font-medium">{normalizeText(event.type)}</TableCell>
                      <TableCell>
                        <ConsentBadge state={event.state} />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {event.personId}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {event.accountId}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {event.placementId}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono font-normal">
                          {event.copyVersion}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="events" cappedAt={LIMIT} />
            {events.data?.length === 0 && (
              <EmptyState
                title="No consent events yet"
                description="Entries appear as soon as a sponsored unit asks someone for consent."
                icon={ShieldCheck}
              />
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}
